import { COLOR_TOKENS } from '@backrs_ui/common-ui/src/designSystem/colors/colors';

const CELL_STYLE = `border: 1px solid ${COLOR_TOKENS['Gray/300']}; padding: 6px 10px; text-align: left; vertical-align: top;`;
const HEADER_STYLE = `${CELL_STYLE} font-weight: bold; background-color: ${COLOR_TOKENS['Gray/100']};`;

const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const cellText = (cell: Element) => (cell.textContent ?? '').replace(/\s+/g, ' ').trim();

const tableCells = (table: HTMLTableElement) =>
  Array.from(table.rows).map((row) => Array.from(row.cells));

const csvField = (text: string) => (/[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text);

/**
 * Copies a table as plain bordered HTML for documents and as tab-separated
 * text, so it pastes as a grid into spreadsheets too.
 */
export const copyTable = async (table: HTMLTableElement): Promise<boolean> => {
  const rows = tableCells(table);

  const html = `<table style="border-collapse: collapse;">${rows
    .map(
      (cells) =>
        `<tr>${cells
          .map((cell) => {
            const tag = cell.tagName === 'TH' ? 'th' : 'td';
            const style = tag === 'th' ? HEADER_STYLE : CELL_STYLE;

            return `<${tag} style="${style}">${escapeHtml(cellText(cell))}</${tag}>`;
          })
          .join('')}</tr>`,
    )
    .join('')}</table>`;
  const tsv = rows.map((cells) => cells.map(cellText).join('\t')).join('\n');

  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([tsv], { type: 'text/plain' }),
      }),
    ]);

    return true;
  } catch (err) {
    console.error('copyTable: clipboard write failed', err);

    return false;
  }
};

export const downloadTableCsv = (table: HTMLTableElement, fileName: string): Promise<boolean> => {
  try {
    const csv = tableCells(table)
      .map((cells) => cells.map((cell) => csvField(cellText(cell))).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);

    return Promise.resolve(true);
  } catch (err) {
    console.error('downloadTableCsv: export failed', err);

    return Promise.resolve(false);
  }
};
